import { useEffect, useMemo, useRef } from 'react'
import type { Idea } from '../api'
import { IdeaRow } from '../components/IdeaRow'
import { CloseIcon, SearchIcon } from '../components/icons'
import { useStatusPicker } from '../components/pickers'
import { useIdeas } from '../state/ideas'
import { createStore, useStore } from '../state/store'
import './search.css'

/**
 * Search over titles and notes, as you type.
 *
 * The whole list is already on the phone, so this filters it locally rather
 * than asking `/api/ideas?q=` per keystroke: instant, and it works on a train.
 * With nothing typed it shows what changed most recently, which is usually
 * what someone opening search is trying to get back to.
 */
const queryStore = createStore('')

export function searchIdeas(ideas: Idea[], query: string): Idea[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) {
    return [...ideas].sort((a, b) => b.updated_at.localeCompare(a.updated_at)).slice(0, 12)
  }
  const scored = ideas
    .map((idea) => {
      const title = idea.title.toLowerCase()
      const body = idea.body.toLowerCase()
      if (!words.every((word) => title.includes(word) || body.includes(word))) return null
      const inTitle = words.filter((word) => title.includes(word)).length
      return { idea, score: inTitle * 2 + (title.startsWith(words[0]) ? 1 : 0) }
    })
    .filter((hit): hit is { idea: Idea; score: number } => hit !== null)
  return scored.sort((a, b) => b.score - a.score).map((hit) => hit.idea)
}

export function Search() {
  const { ideas } = useIdeas()
  const query = useStore(queryStore)
  const input = useRef<HTMLInputElement>(null)
  const status = useStatusPicker()
  const results = useMemo(() => searchIdeas(ideas, query), [ideas, query])

  useEffect(() => {
    // A laptop gets the cursor straight away. A phone would throw the
    // keyboard over the recent list the moment the tab opens, so it waits
    // for a tap on the field.
    if (window.matchMedia('(pointer: fine)').matches) input.current?.focus()
  }, [])

  return (
    <main className="screen search">
      <header className="pipeline-head">
        <h1 className="pipeline-title">Search</h1>
      </header>
      <div className="search-bar">
        <label className="search-field">
          <SearchIcon size={18} />
          <input
            ref={input}
            type="search"
            enterKeyHint="search"
            placeholder="Titles and notes"
            aria-label="Search ideas"
            value={query}
            onChange={(event) => queryStore.set(event.target.value)}
          />
          {query && (
            <button
              type="button"
              className="search-clear"
              aria-label="Clear search"
              onClick={() => {
                queryStore.set('')
                input.current?.focus()
              }}
            >
              <CloseIcon size={16} />
            </button>
          )}
        </label>
      </div>

      <h2 className="search-caption">
        {query.trim() ? `${results.length} ${results.length === 1 ? 'match' : 'matches'}` : 'Recently changed'}
      </h2>
      {results.length > 0 ? (
        <ul className="idea-list">
          {results.map((idea) => (
            <IdeaRow key={idea.id} idea={idea} onStatusTap={status.pick} />
          ))}
        </ul>
      ) : (
        <p className="empty">Nothing matches “{query.trim()}”.</p>
      )}
      {status.element}
    </main>
  )
}
